import { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  Box,
  Text,
  Flex,
  Icon,
  VStack,
  HStack,
  Progress,
} from "@chakra-ui/react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Wind, Layout, Newspaper, Heart, Coffee, Sparkles } from "lucide-react";
import styled, { useTheme } from "styled-components";
import { AppTheme } from "../../theme";
import { THEME } from "../../helpers/constans";
import { BuyCoffeeButton } from "../BuyCoffeeButton";

const WIZARD_STORAGE_KEY = "welcomeWizardCompleted";

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

const StyledModalContent = styled(ModalContent)<{ $theme: AppTheme }>`
  background: ${({ $theme }) => $theme.backgroundCard};
  border: 1px solid ${({ $theme }) => $theme.border};
  border-radius: ${THEME.borderRadiusXl};
  box-shadow: ${({ $theme }) => $theme.shadowXl};
  max-width: 500px;
  overflow: hidden;
`;

const SlideContainer = styled(Box)<{ $theme: AppTheme }>`
  padding: 2rem;
  min-height: 400px;
  display: flex;
  flex-direction: column;
`;

const SlideTitle = styled(Text)<{ $theme: AppTheme }>`
  font-family: 'Outfit', sans-serif;
  font-size: 1.75rem;
  font-weight: 800;
  color: ${({ $theme }) => $theme.text};
  margin-bottom: 0.5rem;
  letter-spacing: -0.02em;
`;

const SlideDescription = styled(Text)<{ $theme: AppTheme }>`
  font-family: 'Outfit', sans-serif;
  font-size: 1rem;
  color: ${({ $theme }) => $theme.textMuted};
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const FeatureCard = styled(Box)<{ $theme: AppTheme }>`
  padding: 1.5rem;
  background: ${({ $theme }) => $theme.backgroundMuted};
  border: 1px solid ${({ $theme }) => $theme.border};
  border-radius: ${THEME.borderRadiusLg};
  margin-bottom: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${({ $theme }) => $theme.primary};
    transform: translateY(-2px);
    box-shadow: ${({ $theme }) => $theme.shadowMd};
  }
`;

const CloseButton = styled.button<{ $theme: AppTheme }>`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ $theme }) => $theme.backgroundMuted};
  border: 1px solid ${({ $theme }) => $theme.border};
  color: ${({ $theme }) => $theme.textMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  
  &:hover {
    background: ${({ $theme }) => $theme.red};
    color: white;
    border-color: ${({ $theme }) => $theme.red};
  }
`;

type Slide = {
  id: number;
  title: string;
  description: string | React.ReactNode;
  icon: React.ComponentType<any>;
};

export function WelcomeWizard() {
  const theme = useTheme() as AppTheme;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Sprawdź czy wizard był już ukończony
    const isCompleted = localStorage.getItem(WIZARD_STORAGE_KEY) === "true";
    if (!isCompleted) {
      setIsOpen(true);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem(WIZARD_STORAGE_KEY, "true");
    setIsOpen(false);
  };

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      handleClose();
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const slides: Slide[] = [
    {
      id: 0,
      title: "Witaj w nowej wersji! 🎉",
      description: (
        <>
          Aplikacja <strong>Śmieciarka Jedzie</strong> została zaktualizowana z nowym, 
          nowoczesnym wyglądem i jest gotowa na rok <strong>2026</strong>!
          <br /><br />
          Odkryj nowe funkcje i ulepszenia, które ułatwią Ci zarządzanie harmonogramem 
          wywozu śmieci.
        </>
      ),
      icon: Sparkles,
    },
    {
      id: 1,
      title: "Nowe funkcje ✨",
      description: (
        <VStack spacing={3} align="stretch" mt={4}>
          <FeatureCard $theme={theme}>
            <Flex align="center" gap={3} mb={2}>
              <Box
                p={2}
                borderRadius={THEME.borderRadius}
                bg={`${theme.primary}15`}
                color={theme.primary}
              >
                <Icon as={Wind} boxSize={5} />
              </Box>
              <Text fontWeight="700" fontSize="lg" color={theme.text}>
                Jakość powietrza
              </Text>
            </Flex>
            <Text fontSize="sm" color={theme.textMuted}>
              Sprawdzaj aktualną jakość powietrza w Twojej gminie w czasie rzeczywistym.
            </Text>
          </FeatureCard>

          <FeatureCard $theme={theme}>
            <Flex align="center" gap={3} mb={2}>
              <Box
                p={2}
                borderRadius={THEME.borderRadius}
                bg={`${theme.brand}15`}
                color={theme.brand}
              >
                <Icon as={Layout} boxSize={5} />
              </Box>
              <Text fontWeight="700" fontSize="lg" color={theme.text}>
                Widgety na stronie głównej
              </Text>
            </Flex>
            <Text fontSize="sm" color={theme.textMuted}>
              Dodawaj widgety z danymi o jakości powietrza bezpośrednio na stronie głównej.
            </Text>
          </FeatureCard>

          <FeatureCard $theme={theme}>
            <Flex align="center" gap={3} mb={2}>
              <Box
                p={2}
                borderRadius={THEME.borderRadius}
                bg={`${theme.accent}15`}
                color={theme.accent}
              >
                <Icon as={Newspaper} boxSize={5} />
              </Box>
              <Text fontWeight="700" fontSize="lg" color={theme.text}>
                Aktualności
              </Text>
            </Flex>
            <Text fontSize="sm" color={theme.textMuted}>
              Bądź na bieżąco z najnowszymi informacjami i aktualizacjami aplikacji.
            </Text>
          </FeatureCard>
        </VStack>
      ),
      icon: Layout,
    },
    {
      id: 2,
      title: "O aplikacji 💙",
      description: (
        <VStack spacing={4} align="stretch" mt={4}>
          <Text fontSize="md" color={theme.text} lineHeight="1.7">
            <strong>Śmieciarka Jedzie</strong> to niezależna aplikacja stworzona z myślą 
            o mieszkańcach, aby ułatwić zarządzanie harmonogramem wywozu śmieci.
          </Text>
          <Box
            p={4}
            borderRadius={THEME.borderRadiusLg}
            bg={`${theme.primary}10`}
            border={`1px solid ${theme.primary}30`}
          >
            <Flex align="center" gap={3} mb={3}>
              <Icon as={Heart} boxSize={5} color={theme.primary} />
              <Text fontWeight="700" fontSize="lg" color={theme.text}>
                Wsparcie projektu
              </Text>
            </Flex>
            <Text fontSize="sm" color={theme.textMuted} mb={3}>
              Jeśli aplikacja jest dla Ciebie przydatna, rozważ wsparcie autora poprzez 
              postawienie kawy. Każda pomoc jest mile widziana! ☕
            </Text>
            <Flex justify="center">
              <BuyCoffeeButton />
            </Flex>
          </Box>
        </VStack>
      ),
      icon: Heart,
    },
  ];

  if (!isOpen) return null;

  const progress = ((currentSlide + 1) / slides.length) * 100;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md" closeOnOverlayClick={false}>
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(8px)" />
      <StyledModalContent $theme={theme}>
        <CloseButton $theme={theme} onClick={handleClose} aria-label="Zamknij">
          <X size={16} />
        </CloseButton>

        <Box position="relative" pt={2} px={2}>
          <Progress
            value={progress}
            size="sm"
            colorScheme="blue"
            borderRadius="full"
            bg={theme.backgroundMuted}
          />
          <Text
            fontSize="xs"
            color={theme.textMuted}
            textAlign="center"
            mt={2}
            mb={1}
            fontFamily="'Outfit', sans-serif"
          >
            {currentSlide + 1} / {slides.length}
          </Text>
        </Box>

        <ModalBody p={0}>
          <AnimatePresence>
            <MotionVStack
              key={currentSlide}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              spacing={0}
            >
              <SlideContainer $theme={theme}>
                <Flex align="center" gap={3} mb={4}>
                  <Box
                    p={3}
                    borderRadius={THEME.borderRadiusLg}
                    bg={`${theme.primary}15`}
                    color={theme.primary}
                  >
                    <Icon as={slides[currentSlide].icon} boxSize={6} />
                  </Box>
                </Flex>

                <SlideTitle $theme={theme}>{slides[currentSlide].title}</SlideTitle>
                <SlideDescription $theme={theme}>
                  {slides[currentSlide].description}
                </SlideDescription>

                <Box flex={1} />
              </SlideContainer>
            </MotionVStack>
          </AnimatePresence>
        </ModalBody>

        <Box p={4} borderTop={`1px solid ${theme.border}`}>
          <HStack spacing={3} justify="space-between">
            <Button
              variant="ghost"
              onClick={handlePrevious}
              isDisabled={currentSlide === 0}
              color={theme.textMuted}
              _hover={{ bg: theme.backgroundMuted }}
            >
              Wstecz
            </Button>

            <Button
              onClick={handleNext}
              bg={theme.primary}
              color="white"
              _hover={{ bg: theme.brand || theme.primary }}
              fontFamily="'Outfit', sans-serif"
              fontWeight="600"
            >
              {currentSlide === slides.length - 1 ? "Zakończ" : "Dalej"}
            </Button>
          </HStack>
        </Box>
      </StyledModalContent>
    </Modal>
  );
}

